// frontend/app/Register.jsx
// Multi-step registration flow: Role → Plan → Sign Up
import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  Linking,
  ActivityIndicator,
} from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import { useNavigation, useRoute } from "@react-navigation/native";
import { FASTAPI_BASE_URL } from "./config";
import { CURRICULA, BOARD_METADATA } from "./constants/boards";

const API_SELF_REGISTER = `${FASTAPI_BASE_URL}/signup/self-register`;
const API_RESEND_VERIFICATION = `${FASTAPI_BASE_URL}/signup/resend-verification`;
const RESEND_COOLDOWN_SECONDS = 60;

// ─── Data ────────────────────────────────────────────────────────────────────

const ROLES = [
  {
    id: "student",
    label: "Student",
    icon: "school",
    description: "Learn with interactive 3D lessons, quizzes, and progress tracking built for you.",
  },
  {
    id: "teacher",
    label: "Teacher",
    icon: "dashboard",
    description: "Manage classes, assign 3D lessons, and track your students' progress.",
  },
  {
    id: "parent",
    label: "Parent",
    icon: "escalator-warning",
    description: "Monitor your child's progress, manage subscriptions, and set learning goals.",
  },
];

// ─── Pricing helpers ─────────────────────────────────────────────────────────

const FREE_TRIAL_PLAN = {
  code: "free",
  name: "Free trial",
  duration_days: 30,
  price_cents: 0,
  currency: "USD",
  region: null,
};


function formatPriceCents(cents) {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

function getPeriodLabel(days) {
  const map = { 30: "/mo", 90: "/quarter", 180: "/6 months", 365: "/year" };
  return map[days] || `/${days} days`;
}

function buildPlanList(apiResponse) {
  const raw = Array.isArray(apiResponse?.plans) ? apiResponse.plans : [];
  const withFree = raw.some((p) => p.code === "free") ? raw : [FREE_TRIAL_PLAN, ...raw];
  return withFree.map((p) => ({
    id: p.code,
    label: p.name,
    priceDisplay: formatPriceCents(p.price_cents),
    periodDisplay: getPeriodLabel(p.duration_days),
    durationDays: p.duration_days,
    priceCents: p.price_cents,
    badge: null,
    badgeColor: null,
    features: [],
  }));
}

const GENDER_OPTIONS = ["Male", "Female", "Other", "Prefer not to say"];

const COUNTRY_OPTIONS = [
  { iso: "US", name: "United States", dial: "+1" },
  { iso: "IN", name: "India", dial: "+91" },
  { iso: "GB", name: "United Kingdom", dial: "+44" },
  { iso: "CA", name: "Canada", dial: "+1" },
  { iso: "AU", name: "Australia", dial: "+61" },
  { iso: "AE", name: "United Arab Emirates", dial: "+971" },
  { iso: "SG", name: "Singapore", dial: "+65" },
  { iso: "ZA", name: "South Africa", dial: "+27" },
  { iso: "DE", name: "Germany", dial: "+49" },
  { iso: "FR", name: "France", dial: "+33" },
  { iso: "BR", name: "Brazil", dial: "+55" },
  { iso: "MX", name: "Mexico", dial: "+52" },
];

function getDefaultCountryIso() {
  try {
    if (typeof navigator !== "undefined" && navigator.language) {
      const parts = String(navigator.language).split("-");
      if (parts.length > 1) {
        const region = (parts[1] || "").toUpperCase();
        if (COUNTRY_OPTIONS.some((c) => c.iso === region)) return region;
      }
    }
  } catch {}
  return "US";
}

function normalizeRedirectUrl(raw) {
  const value = (raw || "").trim();
  if (!value) return "";
  const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(value);
  const withScheme = hasScheme ? value : `https://${value}`;
  try {
    const u = new URL(withScheme);
    if (u.protocol !== "http:" && u.protocol !== "https:") return "";
    return u.toString();
  } catch {
    return "";
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function Register() {
  const nav = useNavigation();
  const route = useRoute();

  // Wizard state
  const [step, setStep] = useState(1);
  const [role, setRole] = useState(null);
  const [plan, setPlan] = useState("free");
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState("");

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countryIso, setCountryIso] = useState(getDefaultCountryIso());
  const [contactNumber, setContactNumber] = useState("");
  const [grade, setGrade] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [contentDb, setContentDb] = useState("");
  const [children, setChildren] = useState([]);

  // Submission
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  // Step 4: resend verification email
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [resendIsError, setResendIsError] = useState(false);

  // Registration just sent an email, so the resend button starts on cooldown.
  useEffect(() => {
    if (step === 4) setResendCooldown(RESEND_COOLDOWN_SECONDS);
  }, [step]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResendVerification = async () => {
    if (resendBusy || resendCooldown > 0) return;
    setResendBusy(true);
    setResendMessage("");
    setResendIsError(false);
    try {
      const res = await fetch(API_RESEND_VERIFICATION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setResendIsError(true);
        const retryAfter = Number(data?.detail?.retry_after_seconds);
        if (Number.isFinite(retryAfter) && retryAfter > 0) {
          setResendCooldown(Math.ceil(retryAfter));
        }
        setResendMessage(
          String(
            data?.detail?.message ||
              data?.detail ||
              "Could not resend the verification email. Please try again."
          )
        );
        return;
      }
      setResendIsError(false);
      setResendMessage("Verification email sent. Check your inbox.");
      setResendCooldown(Number(data?.cooldown_seconds) || RESEND_COOLDOWN_SECONDS);
    } catch {
      setResendIsError(true);
      setResendMessage("Network error while resending the verification email.");
    } finally {
      setResendBusy(false);
    }
  };

  const selectedCountry = useMemo(
    () => COUNTRY_OPTIONS.find((c) => c.iso === countryIso) || COUNTRY_OPTIONS[0],
    [countryIso]
  );

  const redirectFromRoute = route?.params?.redirect || "";
  const redirectFromUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    try {
      const u = new URL(window.location.href);
      return u.searchParams.get("redirect") || "";
    } catch {
      return "";
    }
  }, []);
  const redirectUrl = useMemo(
    () => normalizeRedirectUrl(redirectFromRoute || redirectFromUrl),
    [redirectFromRoute, redirectFromUrl]
  );

  // Geolocation-based country detection
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(pos.coords.latitude)}&lon=${encodeURIComponent(pos.coords.longitude)}`;
          const res = await fetch(url, { headers: { Accept: "application/json" } });
          if (!res.ok) return;
          const data = await res.json();
          const iso = String(data?.address?.country_code || "").toUpperCase();
          if (iso && COUNTRY_OPTIONS.some((c) => c.iso === iso)) setCountryIso(iso);
        } catch {}
      },
      () => {},
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 600000 }
    );
  }, []);

  useEffect(() => {
    const loadPlans = async () => {
      setPlansLoading(true);
      setPlansError("");
      try {
        const params = new URLSearchParams();
        if (role) params.set("role", role);
        const suffix = params.toString() ? `?${params.toString()}` : "";
        const res = await fetch(`${FASTAPI_BASE_URL}/payments/public-plans${suffix}`);
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          setPlansError(json?.detail || "Unable to load plans");
          setPlans(buildPlanList({ plans: [FREE_TRIAL_PLAN] }));
          return;
        }
        const list = buildPlanList(json);
        setPlans(list);
        if (list.length > 0 && !list.find((p) => p.id === plan)) {
          setPlan(list[0].id);
        }
      } catch (e) {
        setPlansError(String(e?.message || e));
        setPlans(buildPlanList({ plans: [FREE_TRIAL_PLAN] }));
      } finally {
        setPlansLoading(false);
      }
    };
    loadPlans();
  }, [role]);

  // ─── Handlers ────────────────────────────────────────────────────────

  const goNext = () => {
    if (step === 1 && role) setStep(2);
    else if (step === 2 && plan) setStep(3);
  };

  const goBack = () => {
    setMessage("");
    setIsError(false);
    if (step > 1) setStep(step - 1);
  };

  const submit = async () => {
    setMessage("");
    setIsError(false);

    if (!firstName.trim() || !lastName.trim()) {
      setIsError(true);
      setMessage("First name and last name are required.");
      return;
    }
    if (!email.trim() || !password) {
      setIsError(true);
      setMessage("Email and password are required.");
      return;
    }
    if (password !== confirmPassword) {
      setIsError(true);
      setMessage("Passwords do not match.");
      return;
    }
    if (password.length < 6) {
      setIsError(true);
      setMessage("Password must be at least 6 characters.");
      return;
    }
    if (age && (!/^\d+$/.test(age) || Number(age) < 1 || Number(age) > 120)) {
      setIsError(true);
      setMessage("Age must be a number between 1 and 120.");
      return;
    }
    if (grade && (!/^\d+$/.test(grade) || Number(grade) < 1 || Number(grade) > 13)) {
      setIsError(true);
      setMessage("Grade must be a number between 1 and 13.");
      return;
    }
    const digits = (contactNumber || "").replace(/\D/g, "");
    if (digits && (digits.length < 7 || digits.length > 15)) {
      setIsError(true);
      setMessage("Phone number must be between 7 and 15 digits.");
      return;
    }

    const selectedPlan = plans.find((p) => p.id === plan) || null;
    const selectedPlanCode =
      selectedPlan && selectedPlan.priceCents > 0 && selectedPlan.id !== "free"
        ? selectedPlan.id
        : null;

    if (role === "parent") {
      const cleanedChildren = (children || []).map((c) => ({
        first_name: (c.first_name || "").trim(),
        last_name: (c.last_name || "").trim(),
        email: (c.email || "").trim().toLowerCase(),
        password: c.password || "",
        grade: c.grade ? Number.parseInt(c.grade, 10) : null,
        gender: (c.gender || "").trim() || null,
        age: c.age ? Number.parseInt(c.age, 10) : null,
      }));

      if (cleanedChildren.length > 2) {
        setIsError(true);
        setMessage("Parents can add up to 2 children.");
        return;
      }
      for (const c of cleanedChildren) {
        if (!c.first_name || !c.last_name || !c.email || !c.password) {
          setIsError(true);
          setMessage("Each child needs first name, last name, email, and password.");
          return;
        }
      }

      const body = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim().toLowerCase(),
        password,
        country_code: selectedCountry.dial,
        contact_number: digits || null,
        grade: grade ? Number.parseInt(grade, 10) : 9,
        gender: (gender || "").trim() || null,
        duration: selectedPlan?.durationDays ?? 30,
        age: age ? Number.parseInt(age, 10) : null,
        content_db: (contentDb || "").trim().toLowerCase() || null,
        role: role,
        selected_plan_code: selectedPlanCode,
        children: cleanedChildren.map((c) => ({
          ...c,
          country_code: selectedCountry.dial,
          contact_number: null,
        })),
      };

      setBusy(true);
      try {
        const res = await fetch(API_SELF_REGISTER, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        let data = null;
        let text = "";
        try {
          data = await res.json();
        } catch {
          text = await res.text();
        }

        if (!res.ok) {
          setIsError(true);
          const errMsg =
            data?.detail?.message ||
            data?.detail ||
            data?.message ||
            text ||
            "Registration failed. Please try again.";
          setMessage(String(errMsg));
          return;
        }

        setIsError(false);
        setStep(4);
      } catch {
        setIsError(true);
        setMessage("Network error while registering.");
      } finally {
        setBusy(false);
      }
      return;
    }

    const body = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim().toLowerCase(),
      password,
      country_code: selectedCountry.dial,
      contact_number: digits || null,
      grade: grade ? Number.parseInt(grade, 10) : 9,
      gender: (gender || "").trim() || null,
      duration: selectedPlan?.durationDays ?? 30,
      age: age ? Number.parseInt(age, 10) : null,
      content_db: (contentDb || "").trim().toLowerCase() || null,
      role: role,
      selected_plan_code: selectedPlanCode,
    };

    setBusy(true);
    try {
      const res = await fetch(API_SELF_REGISTER, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      let data = null;
      let text = "";
      try {
        data = await res.json();
      } catch {
        text = await res.text();
      }

      if (!res.ok) {
        setIsError(true);
        const errMsg =
          data?.detail?.message ||
          data?.detail ||
          data?.message ||
          text ||
          "Registration failed. Please try again.";
        setMessage(String(errMsg));
        return;
      }

      setIsError(false);
      setStep(4);
    } catch {
      setIsError(true);
      setMessage("Network error while registering.");
    } finally {
      setBusy(false);
    }
  };

  // ─── Step Indicator ──────────────────────────────────────────────────

  const STEPS = ["Your role", "Choose plan", "Sign up"];

  const renderStepIndicator = () => (
    <View style={styles.stepRow}>
      {STEPS.map((label, i) => {
        const stepNum = i + 1;
        const isCompleted = step > stepNum;
        const isActive = step === stepNum;
        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <View
                style={[
                  styles.stepLine,
                  step > stepNum && styles.stepLineCompleted,
                ]}
              />
            )}
            <View style={styles.stepItem}>
              <View
                style={[
                  styles.stepCircle,
                  isActive && styles.stepCircleActive,
                  isCompleted && styles.stepCircleCompleted,
                ]}
              >
                {isCompleted ? (
                  <MaterialIcons name="check" size={16} color="#fff" />
                ) : (
                  <Text
                    style={[
                      styles.stepNumber,
                      isActive && styles.stepNumberActive,
                    ]}
                  >
                    {stepNum}
                  </Text>
                )}
              </View>
              <Text
                style={[
                  styles.stepLabel,
                  isActive && styles.stepLabelActive,
                ]}
              >
                {label}
              </Text>
            </View>
          </React.Fragment>
        );
      })}
    </View>
  );

  // ─── Step 1: Role ────────────────────────────────────────────────────

  const renderStep1 = () => (
    <View style={styles.stepContent}>
      <Text style={styles.heading}>Who are you?</Text>
      <Text style={styles.subheading}>
        We'll tailor your experience based on your role.
      </Text>

      <View style={styles.cardsRow}>
        {ROLES.map((r) => {
          const selected = role === r.id;
          return (
            <TouchableOpacity
              key={r.id}
              style={[styles.roleCard, selected && styles.roleCardSelected]}
              onPress={() => setRole(r.id)}
              activeOpacity={0.7}
            >
              <View style={[styles.roleIconWrap, selected && styles.roleIconWrapSelected]}>
                <MaterialIcons
                  name={r.icon}
                  size={28}
                  color={selected ? "#3b82f6" : "#64748b"}
                />
              </View>
              <Text style={[styles.roleTitle, selected && styles.roleTitleSelected]}>
                {r.label}
              </Text>
              <Text style={styles.roleDesc}>{r.description}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      <TouchableOpacity
        style={[styles.btnPrimary, !role && styles.btnDisabled]}
        onPress={goNext}
        disabled={!role}
        activeOpacity={0.7}
      >
        <Text style={[styles.btnPrimaryText, !role && styles.btnDisabledText]}>
          Continue
        </Text>
      </TouchableOpacity>
    </View>
  );

  // ─── Step 2: Plan ────────────────────────────────────────────────────

  const renderStep2 = () => (
    <View style={styles.stepContent}>
      <Text style={styles.heading}>Choose a plan</Text>
      <Text style={styles.subheading}>
        Start free — no credit card required. Upgrade anytime.
      </Text>

      {/* What's included */}
      <View style={styles.includedBox}>
        <Text style={styles.includedTitle}>All plans include</Text>
        <View style={styles.includedGrid}>
          {[
            { icon: "view-in-ar", text: "Interactive 3D lessons" },
            { icon: "quiz", text: "Quizzes & assessments" },
            { icon: "insights", text: "Progress dashboard" },
            { icon: "menu-book", text: "All curriculum topics" },
            { icon: "smart-toy", text: "AI-powered tutoring" },
          ].map((item) => (
            <View key={item.text} style={styles.includedItem}>
              <MaterialIcons name={item.icon} size={20} color="#3b82f6" />
              <Text style={styles.includedText}>{item.text}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Plan cards */}
      <View style={styles.planCardsRow}>
        {plansLoading ? (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" color="#3b82f6" />
            <Text style={styles.loadingText}>Loading plans...</Text>
          </View>
        ) : (
          plans.map((p) => {
            const selected = plan === p.id;
            return (
              <TouchableOpacity
                key={p.id}
                style={[
                  styles.planCard,
                  selected && styles.planCardSelected,
                ]}
                onPress={() => setPlan(p.id)}
                activeOpacity={0.7}
              >
                {p.badge && (
                  <View style={[styles.planBadge, { backgroundColor: p.badgeColor }]}>
                    <Text style={styles.planBadgeText}>{p.badge}</Text>
                  </View>
                )}
                <Text style={styles.planLabel}>{p.label}</Text>
                <View style={styles.planPriceRow}>
                  <Text style={styles.planPrice}>{p.priceDisplay}</Text>
                  <Text style={styles.planPeriod}> {p.periodDisplay}</Text>
                </View>
              </TouchableOpacity>
            );
          })
        )}
      </View>
      {!!plansError && (
        <Text style={styles.planErrorText}>{plansError}</Text>
      )}

      <View style={styles.bottomRow}>
        <TouchableOpacity style={styles.btnBack} onPress={goBack} activeOpacity={0.7}>
          <Text style={styles.btnBackText}>Back</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.btnPrimary, styles.btnGrow, !plan && styles.btnDisabled]}
          onPress={goNext}
          disabled={!plan}
          activeOpacity={0.7}
        >
          <Text style={[styles.btnPrimaryText, !plan && styles.btnDisabledText]}>
            {plan === "free" ? "Continue — no credit card required" : "Continue"}
          </Text>
        </TouchableOpacity>
      </View>
      <Text style={styles.finePrint}>
        {plan === "free"
          ? "No payment info needed to start · Cancel anytime"
          : "You'll enter payment details after creating your account"}
      </Text>
    </View>
  );

  // ─── Step 3: Form ────────────────────────────────────────────────────

  const rolePlanLabel = `${(ROLES.find((r) => r.id === role)?.label) || ""} · ${(plans.find((p) => p.id === plan)?.label) || ""}`;
  const showGrade = role === "student" || role === "teacher";
  const gradeLabel =
    role === "teacher"
      ? "Grade level you teach (optional)"
      : "Your grade level (optional)";

  const updateChild = (idx, field, value) => {
    setChildren((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c))
    );
  };

  const addChild = () => {
    setChildren((prev) => {
      if (prev.length >= 2) return prev;
      return [
        ...prev,
        {
          first_name: "",
          last_name: "",
          email: "",
          password: "",
          grade: "",
          gender: "",
          age: "",
        },
      ];
    });
  };

  const removeChild = (idx) => {
    setChildren((prev) => prev.filter((_, i) => i !== idx));
  };

  const renderStep3 = () => (
    <View style={styles.stepContent}>
      {/* Selection pill */}
      <View style={styles.selectionPill}>
        <Text style={styles.selectionPillText}>{rolePlanLabel}</Text>
        <TouchableOpacity onPress={() => setStep(2)} activeOpacity={0.7}>
          <Text style={styles.changeLink}>change</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.heading}>Create your account</Text>
      <Text style={styles.subheading}>
        {plan === "free"
          ? "Free to start — we'll never ask for a card upfront."
          : "Create your account, then complete payment on the next step."}
      </Text>

      {/* Name row */}
      <View style={styles.formRow}>
        <View style={styles.fieldHalf}>
          <TextInput
            style={styles.input}
            placeholder="First name"
            placeholderTextColor="#9ca3af"
            value={firstName}
            onChangeText={setFirstName}
          />
        </View>
        <View style={styles.fieldHalf}>
          <TextInput
            style={styles.input}
            placeholder="Last name"
            placeholderTextColor="#9ca3af"
            value={lastName}
            onChangeText={setLastName}
          />
        </View>
      </View>

      {/* Email */}
      <TextInput
        style={styles.input}
        placeholder="Email address"
        placeholderTextColor="#9ca3af"
        value={email}
        onChangeText={setEmail}
        autoCapitalize="none"
        keyboardType="email-address"
      />

      {/* Passwords */}
      <View style={styles.formRow}>
        <View style={styles.fieldHalf}>
          <View style={styles.passwordWrap}>
            <TextInput
              style={styles.passwordInput}
              placeholder="Password"
              placeholderTextColor="#9ca3af"
              secureTextEntry={!showPassword}
              value={password}
              onChangeText={setPassword}
            />
            <TouchableOpacity
              style={styles.showBtn}
              onPress={() => setShowPassword((v) => !v)}
            >
              <Text style={styles.showBtnText}>
                {showPassword ? "hide" : "show"}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
        <View style={styles.fieldHalf}>
          <View style={styles.passwordWrap}>
            <TextInput
              style={styles.passwordInput}
              placeholder="Confirm password"
              placeholderTextColor="#9ca3af"
              secureTextEntry={!showConfirmPassword}
              value={confirmPassword}
              onChangeText={setConfirmPassword}
            />
            <TouchableOpacity
              style={styles.showBtn}
              onPress={() => setShowConfirmPassword((v) => !v)}
            >
              <Text style={styles.showBtnText}>
                {showConfirmPassword ? "hide" : "show"}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* Country + Phone */}
      <View style={styles.formRow}>
        <View style={styles.fieldHalf}>
          {Platform.OS === "web" ? (
            <select
              value={countryIso}
              onChange={(e) => setCountryIso(e.target.value)}
              style={nativeSelectStyle}
            >
              {COUNTRY_OPTIONS.map((c) => (
                <option key={c.iso} value={c.iso}>
                  {`${c.name} (${c.dial})`}
                </option>
              ))}
            </select>
          ) : (
            <TextInput
              style={styles.input}
              value={`${selectedCountry.name} (${selectedCountry.dial})`}
              editable={false}
            />
          )}
        </View>
        <View style={styles.fieldHalf}>
          <TextInput
            style={styles.input}
            placeholder="Phone number (optional)"
            placeholderTextColor="#9ca3af"
            value={contactNumber}
            onChangeText={(v) => setContactNumber(v.replace(/\D/g, ""))}
            keyboardType="phone-pad"
          />
        </View>
      </View>

      {/* Grade (conditional) */}
      {showGrade && (
        <View style={styles.formRow}>
          <View style={styles.fieldHalf}>
            <TextInput
              style={styles.input}
              placeholder={gradeLabel}
              placeholderTextColor="#9ca3af"
              value={grade}
              onChangeText={(v) => setGrade(v.replace(/\D/g, ""))}
              keyboardType="number-pad"
            />
          </View>
          <View style={styles.fieldHalf} />
        </View>
      )}

      {/* Age + Gender */}
      <View style={styles.formRow}>
        <View style={styles.fieldHalf}>
          <TextInput
            style={styles.input}
            placeholder="Age (optional)"
            placeholderTextColor="#9ca3af"
            value={age}
            onChangeText={(v) => setAge(v.replace(/\D/g, ""))}
            keyboardType="number-pad"
          />
        </View>
        <View style={styles.fieldHalf}>
          {Platform.OS === "web" ? (
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              style={nativeSelectStyle}
            >
              <option value="">Gender (optional)</option>
              {GENDER_OPTIONS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          ) : (
            <TextInput
              style={styles.input}
              placeholder="Gender (optional)"
              placeholderTextColor="#9ca3af"
              value={gender}
              editable={false}
            />
          )}
        </View>
      </View>

      {/* Curriculum */}
      {Platform.OS === "web" ? (
        <select
          value={contentDb}
          onChange={(e) => setContentDb(e.target.value)}
          style={nativeSelectStyle}
        >
          <option value="">Curriculum (optional)</option>
          {CURRICULA.map((key) => (
            <option key={key} value={key}>
              {BOARD_METADATA[key]?.label || key} — {BOARD_METADATA[key]?.fullName || key}
            </option>
          ))}
        </select>
      ) : (
        <TextInput
          style={styles.input}
          placeholder="Curriculum (optional)"
          placeholderTextColor="#9ca3af"
          value={contentDb}
          editable={false}
        />
      )}

      {role === "parent" && (
        <View style={styles.childrenSection}>
          <Text style={styles.sectionTitle}>Children (optional, up to 2)</Text>
          <Text style={styles.subheading}>
            Add children now if you want, or skip for now and add them later from Upgrade Plan.
          </Text>
          {children.map((child, idx) => (
            <View key={`child-${idx}`} style={styles.childCard}>
              <View style={styles.childHeader}>
                <Text style={styles.childTitle}>Child {idx + 1}</Text>
                {children.length > 1 && (
                  <TouchableOpacity onPress={() => removeChild(idx)}>
                    <Text style={styles.childRemove}>Remove</Text>
                  </TouchableOpacity>
                )}
              </View>
              <View style={styles.formRow}>
                <View style={styles.fieldHalf}>
                  <TextInput
                    style={styles.input}
                    placeholder="First name"
                    placeholderTextColor="#9ca3af"
                    value={child.first_name}
                    onChangeText={(v) => updateChild(idx, "first_name", v)}
                  />
                </View>
                <View style={styles.fieldHalf}>
                  <TextInput
                    style={styles.input}
                    placeholder="Last name"
                    placeholderTextColor="#9ca3af"
                    value={child.last_name}
                    onChangeText={(v) => updateChild(idx, "last_name", v)}
                  />
                </View>
              </View>
              <TextInput
                style={styles.input}
                placeholder="Child email"
                placeholderTextColor="#9ca3af"
                value={child.email}
                onChangeText={(v) => updateChild(idx, "email", v)}
                autoCapitalize="none"
                keyboardType="email-address"
              />
              <TextInput
                style={styles.input}
                placeholder="Child password"
                placeholderTextColor="#9ca3af"
                value={child.password}
                onChangeText={(v) => updateChild(idx, "password", v)}
                secureTextEntry
              />
              <View style={styles.formRow}>
                <View style={styles.fieldHalf}>
                  <TextInput
                    style={styles.input}
                    placeholder="Grade"
                    placeholderTextColor="#9ca3af"
                    value={child.grade}
                    onChangeText={(v) => updateChild(idx, "grade", v.replace(/\D/g, ""))}
                    keyboardType="number-pad"
                  />
                </View>
                <View style={styles.fieldHalf}>
                  <TextInput
                    style={styles.input}
                    placeholder="Age (optional)"
                    placeholderTextColor="#9ca3af"
                    value={child.age}
                    onChangeText={(v) => updateChild(idx, "age", v.replace(/\D/g, ""))}
                    keyboardType="number-pad"
                  />
                </View>
              </View>
            </View>
          ))}
          <TouchableOpacity
            style={[styles.btnOutline, children.length >= 2 && styles.btnDisabled]}
            onPress={addChild}
            disabled={children.length >= 2}
            activeOpacity={0.7}
          >
            <Text style={styles.btnOutlineText}>Add another child</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Submit */}
      <TouchableOpacity
        style={[styles.btnPrimary, busy && styles.btnDisabled]}
        onPress={submit}
        disabled={busy}
        activeOpacity={0.7}
      >
        {busy ? (
          <ActivityIndicator color="#fff" size="small" />
        ) : (
          <Text style={styles.btnPrimaryText}>
            {plan === "free" ? "Create free account" : "Create account & continue to payment"}
          </Text>
        )}
      </TouchableOpacity>

      {/* Feedback message */}
      {!!message && (
        <Text style={[styles.feedback, isError ? styles.feedbackError : styles.feedbackSuccess]}>
          {message}
        </Text>
      )}

      {/* Bottom nav */}
      <View style={styles.bottomRowSpread}>
        <TouchableOpacity style={styles.btnBack} onPress={goBack} activeOpacity={0.7}>
          <Text style={styles.btnBackText}>Back</Text>
        </TouchableOpacity>
        {!!redirectUrl && (
          <TouchableOpacity
            onPress={() => {
              if (Platform.OS === "web" && typeof window !== "undefined") {
                window.location.assign(redirectUrl);
                return;
              }
              Linking.openURL(redirectUrl);
            }}
          >
            <Text style={styles.websiteLink}>Back to website</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  // ─── Step 4: Verify Email ──────────────────────────────────────────

  const renderStep4 = () => (
    <View style={styles.verifyContainer}>
      <View style={styles.verifyIconWrap}>
        <MaterialIcons name="mark-email-unread" size={48} color="#3b82f6" />
      </View>
      <Text style={styles.verifyHeading}>Check your email</Text>
      <Text style={styles.verifySubtext}>
        We sent a verification link to
      </Text>
      <Text style={styles.verifyEmail}>{email.trim().toLowerCase()}</Text>
      <Text style={styles.verifySubtext}>
        Click the link in the email to verify your account, then follow link.
      </Text>
      <Text style={styles.verifyFinePrint}>
        Didn't get the email? Check your spam folder.
      </Text>
      {!!resendMessage && (
        <Text
          style={[
            styles.feedback,
            resendIsError ? styles.feedbackError : styles.feedbackSuccess,
          ]}
        >
          {resendMessage}
        </Text>
      )}
      <TouchableOpacity
        style={[
          styles.verifyResendBtn,
          (resendBusy || resendCooldown > 0) && styles.btnDisabled,
        ]}
        onPress={handleResendVerification}
        disabled={resendBusy || resendCooldown > 0}
        activeOpacity={0.7}
      >
        {resendBusy ? (
          <ActivityIndicator color="#fff" size="small" />
        ) : (
          <Text
            style={[
              styles.btnPrimaryText,
              resendCooldown > 0 && styles.btnDisabledText,
            ]}
          >
            {resendCooldown > 0
              ? `Resend verification email (${resendCooldown}s)`
              : "Resend verification email"}
          </Text>
        )}
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.verifyLoginBtn}
        onPress={() => nav.replace("Login")}
        activeOpacity={0.7}
      >
        <Text style={styles.loginBtnText}>Go back to login</Text>
      </TouchableOpacity>
    </View>
  );

  // ─── Render ──────────────────────────────────────────────────────────

  if (step === 4) {
    return (
      <ScrollView
        contentContainerStyle={styles.container}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.card}>
          {renderStep4()}
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView
      contentContainerStyle={styles.container}
      keyboardShouldPersistTaps="handled"
    >
      <View style={styles.card}>
        {renderStepIndicator()}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </View>
      <View style={styles.loginRow}>
        <View style={styles.loginDivider} />
        <Text style={styles.loginText}>Already have an account?</Text>
        <View style={styles.loginDivider} />
      </View>
      <TouchableOpacity
        style={styles.loginBtn}
        onPress={() => nav.replace("Login")}
        activeOpacity={0.7}
      >
        <Text style={styles.loginBtnText}>Log in</Text>
      </TouchableOpacity>
      {!!redirectUrl && (
        <TouchableOpacity
          style={styles.backToWebsite}
          onPress={() => {
            if (Platform.OS === "web" && typeof window !== "undefined") {
              window.location.assign(redirectUrl);
              return;
            }
            Linking.openURL(redirectUrl);
          }}
          activeOpacity={0.7}
        >
          <Text style={styles.websiteLink}>Back to website</Text>
        </TouchableOpacity>
      )}
    </ScrollView>
  );
}

// ─── Native select style (web only) ─────────────────────────────────────────

const nativeSelectStyle = {
  width: "100%",
  height: 48,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "#d1d5db",
  borderRadius: 10,
  backgroundColor: "#fff",
  paddingLeft: 14,
  marginBottom: 12,
  fontSize: 15,
  color: "#334155",
  outline: "none",
  appearance: "none",
  WebkitAppearance: "none",
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M3 5l3 3 3-3'/%3E%3C/svg%3E\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 12px center",
  backgroundSize: "12px",
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: "#f3f6fb",
    alignItems: "center",
    paddingVertical: 32,
    paddingHorizontal: 16,
  },
  card: {
    width: "100%",
    maxWidth: 920,
    backgroundColor: "#ffffff",
    borderRadius: 16,
    padding: 32,
    paddingTop: 24,
  },

  // ── Step indicator ──
  stepRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 28,
  },
  stepItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  stepCircle: {
    width: 30,
    height: 30,
    borderRadius: 15,
    borderWidth: 2,
    borderColor: "#d1d5db",
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  stepCircleActive: {
    borderColor: "#3b82f6",
    backgroundColor: "#3b82f6",
  },
  stepCircleCompleted: {
    borderColor: "#16a34a",
    backgroundColor: "#16a34a",
  },
  stepNumber: {
    fontSize: 13,
    fontWeight: "700",
    color: "#9ca3af",
  },
  stepNumberActive: {
    color: "#fff",
  },
  stepLabel: {
    fontSize: 14,
    color: "#9ca3af",
    fontWeight: "500",
    marginRight: 4,
  },
  stepLabelActive: {
    color: "#1e293b",
    fontWeight: "700",
  },
  stepLine: {
    width: 48,
    height: 2,
    backgroundColor: "#d1d5db",
    marginHorizontal: 8,
  },
  stepLineCompleted: {
    backgroundColor: "#16a34a",
  },

  // ── Shared ──
  stepContent: {
    marginTop: 4,
  },
  heading: {
    fontSize: 28,
    fontWeight: "800",
    color: "#1e293b",
    marginBottom: 6,
  },
  subheading: {
    fontSize: 15,
    color: "#64748b",
    marginBottom: 24,
  },
  cardsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: 24,
  },

  // ── Role cards ──
  roleCard: {
    flex: 1,
    minWidth: 220,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 12,
    padding: 20,
    backgroundColor: "#fff",
  },
  roleCardSelected: {
    borderColor: "#3b82f6",
    borderWidth: 2,
    backgroundColor: "#f0f7ff",
  },
  roleIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 10,
    backgroundColor: "#f1f5f9",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 14,
  },
  roleIconWrapSelected: {
    backgroundColor: "#dbeafe",
  },
  roleTitle: {
    fontSize: 17,
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: 6,
  },
  roleTitleSelected: {
    color: "#1d4ed8",
  },
  roleDesc: {
    fontSize: 14,
    color: "#64748b",
    lineHeight: 20,
  },

  // ── Plan cards ──
  planCardsRow: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 24,
  },
  planCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 12,
    padding: 16,
    paddingTop: 18,
    backgroundColor: "#fff",
    alignItems: "center",
  },
  planCardSelected: {
    borderColor: "#3b82f6",
    borderWidth: 2,
  },
  loadingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 8,
  },
  loadingText: {
    color: "#64748b",
    fontSize: 13,
  },
  planErrorText: {
    color: "#dc2626",
    fontSize: 13,
    marginTop: -12,
    marginBottom: 12,
  },
  planBadge: {
    alignSelf: "flex-start",
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 6,
    marginBottom: 10,
  },
  planBadgeText: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "700",
  },
  planLabel: {
    fontSize: 15,
    color: "#64748b",
    fontWeight: "600",
    marginBottom: 4,
  },
  planPriceRow: {
    flexDirection: "row",
    alignItems: "baseline",
    marginBottom: 12,
  },
  planPrice: {
    fontSize: 36,
    fontWeight: "800",
    color: "#1e293b",
  },
  planPeriod: {
    fontSize: 15,
    color: "#64748b",
    fontWeight: "500",
  },
  // ── "All plans include" box ──
  includedBox: {
    backgroundColor: "#f8fafc",
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
  },
  includedTitle: {
    fontSize: 15,
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: 14,
  },
  includedGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  includedItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    width: "48%",
    marginBottom: 6,
  },
  includedText: {
    fontSize: 14,
    color: "#475569",
  },

  // ── Form (Step 3) ──
  selectionPill: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    backgroundColor: "#e2e8f0",
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginBottom: 16,
    gap: 10,
  },
  selectionPillText: {
    fontSize: 14,
    color: "#334155",
    fontWeight: "600",
  },
  changeLink: {
    fontSize: 14,
    color: "#1e293b",
    fontWeight: "700",
    backgroundColor: "#fff",
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 6,
    overflow: "hidden",
  },
  formRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  fieldHalf: {
    flex: 1,
    minWidth: 220,
  },
  input: {
    width: "100%",
    height: 48,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 10,
    marginBottom: 12,
    backgroundColor: "#fff",
    fontSize: 15,
    color: "#1e293b",
  },
  passwordWrap: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 10,
    backgroundColor: "#fff",
    marginBottom: 12,
    height: 48,
  },
  passwordInput: {
    flex: 1,
    height: "100%",
    paddingHorizontal: 14,
    fontSize: 15,
    color: "#1e293b",
  },
  showBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 6,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 6,
    backgroundColor: "#f9fafb",
  },
  showBtnText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#374151",
  },
  childrenSection: {
    marginTop: 6,
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: 8,
  },
  childCard: {
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 12,
    padding: 14,
    backgroundColor: "#f8fafc",
    marginBottom: 12,
  },
  childHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  childTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#1e293b",
  },
  childRemove: {
    fontSize: 13,
    fontWeight: "600",
    color: "#dc2626",
  },
  btnOutline: {
    borderWidth: 1,
    borderColor: "#94a3b8",
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: "center",
    marginBottom: 8,
  },
  btnOutlineText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#475569",
  },

  // ── Buttons ──
  btnPrimary: {
    width: "100%",
    height: 48,
    backgroundColor: "#2563eb",
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 4,
  },
  btnPrimaryText: {
    color: "#fff",
    fontSize: 15,
    fontWeight: "700",
  },
  btnDisabled: {
    backgroundColor: "#e2e8f0",
  },
  btnDisabledText: {
    color: "#9ca3af",
  },
  btnBack: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 10,
    backgroundColor: "#fff",
  },
  btnBackText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#374151",
  },
  btnGrow: {
    flex: 1,
    marginLeft: 12,
  },
  bottomRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 4,
  },
  bottomRowSpread: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 16,
  },
  finePrint: {
    textAlign: "center",
    fontSize: 13,
    color: "#9ca3af",
    marginTop: 12,
  },
  websiteLink: {
    fontSize: 14,
    color: "#3b82f6",
    fontWeight: "600",
  },
  feedback: {
    marginTop: 12,
    textAlign: "center",
    fontSize: 14,
  },
  feedbackError: {
    color: "#dc2626",
  },
  feedbackSuccess: {
    color: "#16a34a",
  },
  loginRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 28,
    marginBottom: 12,
    maxWidth: 920,
    width: "100%",
  },
  loginDivider: {
    flex: 1,
    height: 1,
    backgroundColor: "#d1d5db",
  },
  loginText: {
    fontSize: 14,
    color: "#9ca3af",
    marginHorizontal: 16,
  },
  loginBtn: {
    maxWidth: 920,
    width: "100%",
    height: 48,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#d1d5db",
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
  },
  loginBtnText: {
    fontSize: 15,
    fontWeight: "700",
    color: "#374151",
  },
  backToWebsite: {
    marginTop: 14,
    alignItems: "center",
  },

  // ── Verify email (Step 4) ──
  verifyContainer: {
    alignItems: "center",
    paddingVertical: 32,
    paddingHorizontal: 16,
  },
  verifyIconWrap: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: "#eff6ff",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  verifyHeading: {
    fontSize: 26,
    fontWeight: "800",
    color: "#1e293b",
    marginBottom: 12,
  },
  verifySubtext: {
    fontSize: 15,
    color: "#64748b",
    textAlign: "center",
    lineHeight: 22,
  },
  verifyEmail: {
    fontSize: 16,
    fontWeight: "700",
    color: "#1e293b",
    marginVertical: 8,
  },
  verifyFinePrint: {
    marginTop: 16,
    fontSize: 13,
    color: "#9ca3af",
    textAlign: "center",
  },
  verifyResendBtn: {
    marginTop: 24,
    width: "100%",
    maxWidth: 360,
    height: 48,
    backgroundColor: "#2563eb",
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  verifyLoginBtn: {
    marginTop: 12,
    width: "100%",
    maxWidth: 360,
    height: 48,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#d1d5db",
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
  },
});
